import { Component, ElementRef, Inject, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { DomSanitizer, SafeUrl } from '@angular/platform-browser';
import { Subscription } from 'rxjs';
import { CameraService } from 'src/app/project/shared/services/camera.service';
import { LoginService } from 'src/app/user/shared/services/login.service';
import { environment } from 'src/environments/environment';
import { CameraTypeEnum, getCameraName } from 'src/app/project/shared/enums/camera-type.enum';

@Component({
  selector: 'avs-camera-view-modal',
  standalone: false,
  templateUrl: './camera-view-modal.component.html',
  styleUrl: './camera-view-modal.component.css'
})
export class CameraViewModalComponent implements OnInit, OnDestroy {
  @ViewChild('streamImg', { static: false }) streamImg!: ElementRef<HTMLImageElement>;
  rs: string = ''; // RouteStation ID
  camTitle: string = ''; 
  stationName: string = '';
  camType: CameraTypeEnum = CameraTypeEnum.Unknown; // Camera type
  isCamWithControls: boolean = false;
  snapshotUrl: SafeUrl | null = null;
  currentZoom: number = 1000; // Aktueller Zoom-Level
  private subscriptions: Subscription[] = [];
  streamResponse: any = null;
  // AUTO-REFRESH EIGENSCHAFTEN
  autoRefreshEnabled: boolean = true;
  selectedInterval: number = 3; // Standard: 3 Sekunden
  availableIntervals = [
    { value: 1, label: '1 Sekunden' },
    { value: 3, label: '3 Sekunden' },
    { value: 5, label: '5 Sekunden' }
  ];
  private refreshInterval: any = null;
  lastSnapshotTime: Date | null = null;
  token: string = ''; // JWT-Token für die Authentifizierung
  public timeLeft: any = null;
  private countdownInterval: any = null;
  selectedView = 'Stream'; // Standard Auswahl
  error: string = '';

  positions: { name: string, found: boolean }[] = [{ name: '1', found: false },
  { name: '2', found: false },
  { name: '3', found: false },
  { name: '4', found: false }
  ];


  public viewTimeoutMs = 3 * 60 * 1000;    // 3 Minuten in Millisekunden
  private viewTimeoutHandle: any = null;    // setTimeout-Handle
  public isExpired = false;                // Ablauf erreicht

  public apiAddress = environment.apiAddress;
  constructor(
    private cameraService: CameraService,
    private sanitizer: DomSanitizer,
    public dialogRef: MatDialogRef<CameraViewModalComponent>,
    @Inject(MAT_DIALOG_DATA) public data: any,
    public auth: LoginService
  ) { }


  ngOnInit(): void {
    this.rs = this.data.rs;
    this.camType = this.data.type;
    this.camTitle = getCameraName(this.camType);
    this.stationName = this.data.name
    this.isCamWithControls = (this.camType == CameraTypeEnum.PTZ);
    if (this.isCamWithControls) { this.getCameraPositions() }
    // RouteStation ID aus den Dialog-Daten
    /*  this.startAutoRefresh();  */// Auto-Refresh starten
    this.startViewTimer();
    this.switchToStream();

  }

  // CLEANUP
  ngOnDestroy(): void {
    this.clearViewTimer();
    this.stopAutoRefresh();
    this.subscriptions.forEach(sub => sub.unsubscribe());
    // Object URL freigeben um Memory Leaks zu vermeiden
    if (this.snapshotUrl) {
      URL.revokeObjectURL(this.snapshotUrl as string);
    }
    if (this.streamImg?.nativeElement) {
      this.streamImg.nativeElement.src = '';
    }
  }

  private startViewTimer(): void {
    this.clearViewTimer();
    this.isExpired = false;
    if (this.streamImg?.nativeElement) {
      this.streamImg.nativeElement.src = this.apiAddress + 'camera/stream?rs=' + this.rs + '&token=' + this.auth.getJwtToken();
    }
    this.viewTimeoutHandle = setTimeout(() => {
      this.expireView();
    }, this.viewTimeoutMs);
  }

  private clearViewTimer(): void {
    if (this.viewTimeoutHandle) {
      clearTimeout(this.viewTimeoutHandle);
      this.viewTimeoutHandle = null;
    }
  }

  public onRefreshClicked(): void {
    // Ablauf zurücksetzen und aktuelle Ansicht neu laden
    this.startViewTimer();
    if (this.selectedView === 'Bild') {
      this.startAutoRefresh();
    } else {
      setTimeout(() => {
        this.streamImg.nativeElement.src = this.apiAddress + 'camera/stream?rs=' + this.rs + '&token=' + this.auth.getJwtToken();
      }, 200);
    }
  }

  public switchToSnapshot(): void {
    if (this.isExpired) return;
    this.selectedView = 'Bild';
    /*  this.stopStream();     */              // evtl. Stream stoppen
    this.startAutoRefresh();
    if (this.streamImg?.nativeElement) {
      this.streamImg.nativeElement.src = '';
    }
  }

  public switchToStream(): void {
    /* if (this.isExpired) return; */
    this.selectedView = 'Stream';
    this.stopAutoRefresh();
    setTimeout(() => {
      this.streamImg.nativeElement.src = this.apiAddress + 'camera/stream?rs=' + this.rs + '&token=' + this.auth.getJwtToken();
    }, 200);
  }

  private expireView(): void {
    // Nach Ablauf deaktivieren
    this.isExpired = true;
    if (this.streamImg?.nativeElement) {
      this.streamImg.nativeElement.src = '';
    }
    this.stopAutoRefresh();
    this.streamResponse = null;
  }

  loadSnapshot(): void {
    if (!this.rs) {
      return;
    }

    const sub = this.cameraService.getSnapshot(this.rs).subscribe({
      next: (blob: Blob) => {
        // Vorherige URL freigeben
        if (this.snapshotUrl) {
          URL.revokeObjectURL(this.snapshotUrl as string);
        }
        const objectURL = URL.createObjectURL(blob);
        this.snapshotUrl = this.sanitizer.bypassSecurityTrustUrl(objectURL);
        this.lastSnapshotTime = new Date();
      },
      error: (error) => {
        this.snapshotUrl = null;
        this.lastSnapshotTime = null;
      }
    });
    this.subscriptions.push(sub);
  }

  // AUTO-REFRESH FUNKTIONEN
  toggleAutoRefresh(): void {
    if (this.autoRefreshEnabled) {
      this.startAutoRefresh();
    } else {
      this.stopAutoRefresh();
    }
  }

  onIntervalChange(): void {
    if (this.autoRefreshEnabled) {
      this.stopAutoRefresh();
      this.startAutoRefresh();
    } else {
      this.timeLeft = this.selectedInterval; // fallback
    }
  }

  private startAutoRefresh(): void {
    if (!this.rs) {
      this.autoRefreshEnabled = false;
      return;
    }

    // Ersten Snapshot sofort laden
    this.loadSnapshot();
    // Timer setzen
    this.timeLeft = this.selectedInterval;

    // Countdown-Intervall für timeLeft starten
    this.countdownInterval = setInterval(() => {
      if (this.timeLeft > 0) {
        this.timeLeft--;
      }
    }, 1000);

    // Snapshot-Intervall starten
    this.refreshInterval = setInterval(() => {
      this.loadSnapshot();
      this.timeLeft = this.selectedInterval;
    }, this.selectedInterval * 1000);
  }

  private stopAutoRefresh(): void {
    if (this.refreshInterval) {
      clearInterval(this.refreshInterval);
      this.refreshInterval = null;
    }
    if (this.countdownInterval) {
      clearInterval(this.countdownInterval);
      this.countdownInterval = null;
    }
    this.timeLeft = this.selectedInterval;
  }

  // BEWEGUNGSSTEUERUNG (unverändert)
  moveLeft(): void {
    this.moveCamera('left');
  }

  moveRight(): void {
    this.moveCamera('right');
  }

  moveUp(): void {
    this.moveCamera('up');
  }

  moveDown(): void {
    this.moveCamera('down');
  }

  stopMovement(): void {
    this.moveCamera('stop');
  }

  private moveCamera(direction: string): void {
    if (!this.rs) {
      return;
    }
    const sub = this.cameraService.moveCamera(direction, this.rs).subscribe({
      next: (response) => {
      }
    });
    this.subscriptions.push(sub);
  }

  zoomIn(): void {
    this.currentZoom = Math.min(this.currentZoom + 500, 9999);
    this.setZoom();
  }

  zoomOut(): void {
    this.currentZoom = Math.max(this.currentZoom - 500, 1);
    this.setZoom();
  }

  private setZoom(): void {
    if (!this.rs) {
      return;
    }

    const sub = this.cameraService.zoomCamera(this.currentZoom, this.rs).subscribe({
      next: (response) => {
      },
      error: (error) => {
      }
    });
    this.subscriptions.push(sub);
  }

  // UTILITY-METHODEN
  getFormattedLastUpdate(): string {
    if (!this.lastSnapshotTime) return 'Nie';
    return this.lastSnapshotTime.toLocaleTimeString();
  }

  onStreamLoad(): void {
    this.error = '';
  }

  onStreamError(event: any): void {
    this.error = 'Stream-Verbindung fehlgeschlagen';
  }

  public onCloseDialogClicked(): void {
    this.dialogRef.close();

  }

  moveToHome(): void {
    if (!this.rs) {
      return;
    }
    const sub = this.cameraService.gotoCameraHomePosition(this.rs).subscribe({
      next: (response) => {
        // Erfolgreich zur Home-Position bewegt
      }
    });
    this.subscriptions.push(sub);
  }

  goToPosition(positionName: string): void {
    if (!this.rs) {
      return;
    }
    const sub = this.cameraService.gotoCameraPosition(positionName, this.rs).subscribe({
      next: (response) => {
        // Erfolgreich zur gespeicherten Position bewegt
      }
    });

    this.subscriptions.push(sub);
  }

  deletePosition(positionName: string): void {
    if (!this.rs) {
      return;
    }

    const confirmDelete = confirm(`Möchten Sie die Position "${positionName}" wirklich löschen?`);
    if (!confirmDelete) {
      return;
    }

    const sub = this.cameraService.deleteCameraPosition(positionName, this.rs).subscribe({
      next: (response) => {
        this.getCameraPositions()
      }
    });

    this.subscriptions.push(sub);
  }

  saveCurrentPosition(positionName: string): void {
    if (!this.rs) {
      return;
    }
    const confirmSave = confirm(`Möchten Sie die Position "${positionName}" wirklich speichern?`);
    if (!confirmSave) {
      return;
    }
    const sub = this.cameraService.saveCameraPosition(positionName, this.rs).subscribe({
      next: (response) => {
        this.getCameraPositions()
      },
      error: (error) => {
        // Fehlerbehandlung
      }
    });
    this.subscriptions.push(sub);
  }

  getCameraPositions() {
    if (!this.rs) {
      return;
    }
    const sub = this.cameraService.getCameraPositions(this.rs).subscribe({
      next: (positions) => {
        this.checkIfPositionExists(positions);
      }
    });
    this.subscriptions.push(sub);
  }

  checkIfPositionExists(positions: string[]) {
    this.positions.forEach(pos => {
      pos.found = positions.includes(pos.name);
    });
  }

}